// InviteMemberFlow — the bulk invitation (ORP-18889), and the module's best-designed legacy
// behaviour kept intact: A PARTIAL INVITE IS NOT A FAILURE.
//
// `POST /accounts/{a}/users/create` answers `{results:[…]}` on BOTH the 2xx and the 4xx. v3 read
// both bodies (`UserPermissionModal.vue:506-527`) and that is the one thing about the 584-line
// component worth porting verbatim. It lives here rather than in the SFC because it is a six-line
// rule about a wire format, not a rendering concern:
//
//   2xx with `results`     → those results
//   2xx without `results`  → synthesise one `success` per address (v3 `:515-517`)
//   4xx with `results`     → those results, and NOT a thrown failure
//   4xx without `results`  → rethrow; the modal classifies it through `settingsWriteFailure`
//
// The only deviation from v3 is the account re-hydrate on the 4xx-with-results path. v3's `.catch`
// skipped it (`:520-523`), so a response where three of five invitations landed left 07's account
// context stale. The reload is gated on at least one success, so an all-failed batch still costs
// nothing.
import { AccountMemberWritesApi } from '../api/AccountMemberWritesApi'
import type { BulkInvitePayload, InviteResultDto } from '../models/memberInvite.types'
import { inviteResultsFromError } from '../service/inviteResults'

import { reloadAccountContext } from './reloadAccountContext'
import type { MemberFlowOptions } from './RemoveMemberFlow'

import { useAccount } from '@/features/account'

export interface InviteMemberResult {
  /** One row per address, from the server or synthesised. Never empty on a resolved call. */
  results: InviteResultDto[]
  /** The write landed but 07's account re-read did not — a warning, never a failure (§14). */
  refreshFailed: boolean
}

export class InviteMemberFlow {
  private readonly api: AccountMemberWritesApi

  constructor(options: MemberFlowOptions = {}) {
    this.api = options.api ?? new AccountMemberWritesApi()
  }

  async execute(payload: BulkInvitePayload): Promise<InviteMemberResult> {
    const { accountId } = useAccount()
    if (accountId.value === undefined) {
      throw new Error('InviteMemberFlow: no account in scope for an invitation')
    }

    let results: InviteResultDto[]
    try {
      results =
        (await this.api.sendBulkInvite(accountId.value, payload)) ??
        payload.emails.map((email) => ({ email, status: 'success' as const }))
    } catch (cause) {
      const partial = inviteResultsFromError(cause)
      // No `results` in the error body ⇒ nothing was decided per-address. That IS a failure.
      if (!partial) throw cause
      return { results: partial, refreshFailed: !(await this.rehydrate(partial)) }
    }

    return { results, refreshFailed: !(await this.rehydrate(results)) }
  }

  /** Re-read 07's account context only when something actually changed on the server. */
  private async rehydrate(results: readonly InviteResultDto[]): Promise<boolean> {
    if (!results.some((row) => row.status === 'success')) return true
    return reloadAccountContext()
  }
}
